<template>
  <el-dialog
    :visible.sync="dialogVisible"
    width="80%"
    :close-on-click-modal="false"
    :append-to-body="true"
    :modal="true"
    :modal-append-to-body="true"
    :show-close="false"
    @close="handleClose"
    class="custom-post-answer-dialog"
  >
    <!-- Custom header with title and buttons -->
    <div slot="title" class="dialog-header">
      <span class="dialog-title">{{ isEditing ? 'Edit Answer' : 'Add New Answer' }}</span>
      <div class="dialog-header-buttons">
        <el-button size="small" @click="handleClose">Cancel</el-button>
        <el-button
          size="small"
          type="primary"
          @click.stop.prevent="handleSave"
          :loading="saving"
          :disabled="saving"
        >
          {{ isEditing ? 'Update' : 'Create' }}
        </el-button>
      </div>
    </div>
    <el-form :model="answerForm" :rules="answerRules" ref="answerForm" label-width="0px" class="no-label-form">
      <el-form-item prop="content_type" class="form-item-no-label content-type-row">
        <div style="display: flex; gap: 20px; align-items: center;">
          <el-radio-group v-model="answerForm.content_type" @change="handleContentTypeChange" size="medium">
            <el-radio-button label="text">Text</el-radio-button>
            <el-radio-button label="quote">Quote</el-radio-button>
            <el-radio-button label="image">Image</el-radio-button>
            <el-radio-button label="video">Video</el-radio-button>
            <el-radio-button label="youtube">YouTube</el-radio-button>
            <el-radio-button label="quiz">Quiz</el-radio-button>
          </el-radio-group>

          <!-- Editor Type Selector - Show only when Text is selected -->
          <el-radio-group v-if="answerForm.content_type === 'text'" v-model="editorType" size="medium">
            <el-radio-button label="plain">Plain Text</el-radio-button>
            <el-radio-button label="markdown">Markdown</el-radio-button>
            <el-radio-button label="richtext">Rich Text</el-radio-button>
          </el-radio-group>
        </div>
      </el-form-item>

      <!-- Text Content -->
      <template v-if="answerForm.content_type === 'text'">
        <!-- Plain Text Editor -->
        <el-form-item v-if="editorType === 'plain'" prop="content" class="content-textarea-item form-item-no-label">
          <!-- Audio Recording Button -->
          <div class="audio-recording-wrapper">
            <Translate 
              :needTranslate="false" 
              @addText="addTextToContent" 
              startBtnText="Start recording" 
              endBtnText="Stop recording"
              :showAudioWaveform="true"
              :showDetailConfig="false"
              :showConfigDialog="false"
            />
          </div>
          <el-input
            type="textarea"
            v-model="answerForm.content"
            placeholder="Enter your answer content or use voice recording..."
            :autosize="{ minRows: 10, maxRows: 20 }"
            maxlength="5000"
            show-word-limit
          ></el-input>
        </el-form-item>

        <!-- Markdown Editor -->
        <el-form-item v-else-if="editorType === 'markdown'" prop="content" class="markdown-editor-item form-item-no-label">
          <!-- Audio Recording Button -->
          <div class="audio-recording-wrapper">
            <Translate 
              :needTranslate="false" 
              @addText="addTextToContent" 
              startBtnText="Start recording" 
              endBtnText="Stop recording"
              :showAudioWaveform="true"
              :showDetailConfig="false"
              :showConfigDialog="false"
            />
          </div>
          <mavon-editor
            v-model="answerForm.content"
            :toolbars="{
              bold: true,
              italic: true,
              header: true,
              underline: true,
              strikethrough: true,
              mark: true,
              superscript: true,
              subscript: true,
              quote: true,
              ol: true,
              ul: true,
              link: true,
              imagelink: true,
              code: true,
              table: true,
              fullscreen: true,
              readmodel: true,
              htmlcode: true,
              help: true,
              undo: true,
              redo: true,
              trash: true,
              save: false,
              navigation: true,
              alignleft: true,
              aligncenter: true,
              alignright: true,
              subfield: true,
              preview: true
            }"
            style="min-height: 400px;"
          />
        </el-form-item>

        <!-- Rich Text Editor -->
        <el-form-item v-else-if="editorType === 'richtext'" prop="content" class="richtext-editor-item form-item-no-label">
          <!-- Audio Recording Button -->
          <div class="audio-recording-wrapper">
            <Translate 
              :needTranslate="false" 
              @addText="addTextToContent" 
              startBtnText="Start recording" 
              endBtnText="Stop recording"
              :showAudioWaveform="true"
              :showDetailConfig="false"
              :showConfigDialog="false"
            />
          </div>
          <quill-editor
            v-model="answerForm.content"
            :options="quillOptions"
            style="min-height: 400px;"
          />
        </el-form-item>
      </template>

      <!-- Quote Content -->
      <template v-else-if="answerForm.content_type === 'quote'">
        <el-form-item prop="content" class="content-textarea-item form-item-no-label">
          <div class="audio-recording-wrapper">
            <Translate
              :needTranslate="false"
              @addText="addTextToContent"
              startBtnText="Start recording"
              endBtnText="Stop recording"
              :showAudioWaveform="true"
              :showDetailConfig="false"
              :showConfigDialog="false"
            />
          </div>
          <el-input
            type="textarea"
            v-model="answerForm.content"
            placeholder="Enter the quote text..."
            :autosize="{ minRows: 8, maxRows: 16 }"
            maxlength="2000"
            show-word-limit
          ></el-input>
        </el-form-item>
      </template>

      <!-- Image Content -->
      <template v-else-if="answerForm.content_type === 'image'">
        <el-form-item prop="fileList" class="form-item-no-label">
          <div class="image-upload-container" @click="triggerFileInput">
            <input ref="fileInput" type="file" @change="handleImageChange" accept="image/*" style="display: none;" />
            <div class="upload-area" :class="{ 'has-image': imageUrl }">
              <img v-if="imageUrl" :src="imageUrl" class="uploaded-image" alt="Uploaded image">
              <div v-else class="upload-placeholder">
                <i class="el-icon-plus upload-icon"></i>
                <p class="upload-text">Click to upload image</p>
              </div>
            </div>
          </div>
          <div class="upload-tip">Only jpg/png files are allowed, and size should be less than 5MB</div>
        </el-form-item>
      </template>

      <!-- Video Content -->
      <template v-else-if="answerForm.content_type === 'video'">
        <el-form-item prop="fileList" class="form-item-no-label">
          <el-upload class="video-uploader" :action="uploadUrl" :headers="uploadHeaders" :data="uploadData"
            :show-file-list="false" :on-success="handleVideoSuccess" :before-upload="beforeVideoUpload"
            accept="video/*">
            <video v-if="videoUrl" :src="videoUrl" controls class="uploaded-video"></video>
            <div v-else class="upload-placeholder">
              <i class="el-icon-video-camera"></i>
              <span>Click to upload video</span>
            </div>
          </el-upload>
          <div class="upload-tip">Only mp4/mov files are allowed, and size should be less than 50MB</div>
        </el-form-item>
      </template>

      <!-- YouTube Content -->
      <template v-else-if="answerForm.content_type === 'youtube'">
        <el-form-item prop="content" class="form-item-no-label">
          <el-input
            v-model="answerForm.content"
            placeholder="Paste a YouTube URL, e.g. https://www.youtube.com/watch?v=VIDEO_ID"
            @input="handleYoutubeUrlInput"
          >
            <i slot="prefix" class="el-icon-video-camera"></i>
          </el-input>
          <div class="upload-tip">Supported: youtube.com/watch?v=..., youtu.be/..., youtube.com/embed/...</div>
          <div v-if="youtubePreviewId" class="youtube-preview">
            <iframe
              :src="'https://www.youtube.com/embed/' + youtubePreviewId"
              frameborder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowfullscreen
              style="width: 100%; aspect-ratio: 16/9; border-radius: 8px; margin-top: 12px;"
            ></iframe>
          </div>
          <div v-else-if="answerForm.content && answerForm.content.trim()" class="youtube-error">
            <i class="el-icon-warning"></i> Could not extract YouTube video ID. Please check the URL.
          </div>
        </el-form-item>
      </template>

      <!-- Quiz Content -->
      <template v-else-if="answerForm.content_type === 'quiz'">
        <el-form-item prop="quiz_content.question" class="form-item-no-label">
          <!-- Audio Recording Button for Quiz Question -->
          <div class="audio-recording-wrapper">
            <Translate 
              :needTranslate="false" 
              @addText="addTextToQuizQuestion" 
              startBtnText="Start recording" 
              endBtnText="Stop recording"
              :showAudioWaveform="true"
              :showDetailConfig="false"
              :showConfigDialog="false"
            />
          </div>
          <el-input
            type="textarea"
            v-model="answerForm.quiz_content.question"
            placeholder="Enter your quiz question or use voice recording..."
            :autosize="{ minRows: 3, maxRows: 6 }"
            maxlength="500"
            show-word-limit
          ></el-input>
        </el-form-item>

        <el-form-item prop="quiz_content.options" class="form-item-no-label">
          <div v-for="(option, index) in answerForm.quiz_content.options" :key="index" class="quiz-option-input">
            <el-input v-model="answerForm.quiz_content.options[index]"
              :placeholder="`Option ${['A', 'B', 'C', 'D'][index]}`" maxlength="200">
              <template slot="prepend">{{ ['A', 'B', 'C', 'D'][index] }}.</template>
            </el-input>
          </div>
        </el-form-item>

        <el-form-item prop="quiz_content.correct_answer" class="form-item-no-label">
          <div class="correct-answer-section">
            <span class="correct-answer-label">Correct Answer:</span>
            <el-radio-group v-model="answerForm.quiz_content.correct_answer">
              <el-radio-button
                v-for="(option, index) in answerForm.quiz_content.options"
                :key="index"
                :label="['A', 'B', 'C', 'D'][index]"
                :disabled="!option || option.trim() === ''"
              >
                {{ ['A', 'B', 'C', 'D'][index] }}
              </el-radio-button>
            </el-radio-group>
          </div>
        </el-form-item>

        <el-form-item prop="quiz_content.explanation" class="form-item-no-label">
          <el-input type="textarea" v-model="answerForm.quiz_content.explanation"
            placeholder="Explain the correct answer..." :autosize="{ minRows: 4, maxRows: 8 }" maxlength="1000"
            show-word-limit></el-input>
        </el-form-item>
      </template>

    </el-form>
  </el-dialog>
</template>

<script>
import * as qiniu from "qiniu-js";
import { postRequest } from "@/apis/front";
import { mavonEditor } from 'mavon-editor';
import 'mavon-editor/dist/css/index.css';
import { quillEditor } from 'vue-quill-editor';
import 'quill/dist/quill.core.css';
import 'quill/dist/quill.snow.css';
import 'quill/dist/quill.bubble.css';
import Translate from "@/components/Translate/index.vue";

export default {
  name: 'EditPostAnswer',
  components: {
    mavonEditor,
    quillEditor,
    Translate
  },
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    answer: {
      type: Object,
      default: () => null
    }
  },
  data() {
    return {
      dialogVisible: false,
      saving: false,
      imageUrl: '',
      videoUrl: '',
      youtubePreviewId: '',
      editorType: 'plain', // 'plain', 'markdown', or 'richtext'
      // Store content for each type to preserve when switching
      contentStore: {
        text: '',
        image: { url: '', fileList: [] },
        video: { url: '', fileList: [] },
        quiz: {
          question: '',
          options: ['', '', '', ''],
          correct_answer: '',
          explanation: ''
        }
      },
      previousContentType: null, // Track previous content type for preservation
      quillOptions: {
        modules: {
          toolbar: [
            ['bold', 'italic', 'underline', 'strike'],
            ['blockquote', 'code-block'],
            [{ 'header': 1 }, { 'header': 2 }],
            [{ 'list': 'ordered' }, { 'list': 'bullet' }],
            [{ 'script': 'sub' }, { 'script': 'super' }],
            [{ 'indent': '-1' }, { 'indent': '+1' }],
            [{ 'direction': 'rtl' }],
            [{ 'size': ['small', false, 'large', 'huge'] }],
            [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
            [{ 'color': [] }, { 'background': [] }],
            [{ 'font': [] }],
            [{ 'align': [] }],
            ['clean'],
            ['link', 'image']
          ]
        },
        placeholder: 'Enter your answer content...'
      },
      answerForm: {
        content_type: 'text',
        content: '',
        fileList: [],
        quiz_content: {
          question: '',
          options: ['', '', '', ''],
          correct_answer: '',
          explanation: ''
        },
        is_hidden: false
      },
      answerRules: {
        content_type: [
          { required: true, message: 'Please select content type', trigger: 'change' }
        ],
        content: [
          {
            required: true,
            validator: (rule, value, callback) => {
              if ((this.answerForm.content_type === 'text' || this.answerForm.content_type === 'quote') && (!value || value.trim() === '')) {
                callback(new Error('Please enter content'));
              } else if (this.answerForm.content_type === 'youtube' && (!value || !this.extractYoutubeId(value))) {
                callback(new Error('Please enter a valid YouTube URL'));
              } else {
                callback();
              }
            },
            trigger: 'blur'
          },
          { min: 1, max: 5000, message: 'Content length should be 1 to 5000 characters', trigger: 'blur' }
        ],
        fileList: [
          {
            required: true,
            validator: (rule, value, callback) => {
              if ((this.answerForm.content_type === 'image' || this.answerForm.content_type === 'video') &&
                (!value || value.length === 0)) {
                callback(new Error(`Please upload a ${this.answerForm.content_type}`));
              } else {
                callback();
              }
            },
            trigger: 'change'
          }
        ],
        'quiz_content.question': [
          { required: true, message: 'Please enter quiz question', trigger: 'blur' },
          { min: 5, max: 500, message: 'Question length should be 5 to 500 characters', trigger: 'blur' }
        ],
        'quiz_content.correct_answer': [
          { required: true, message: 'Please select the correct answer', trigger: 'change' }
        ],
        'quiz_content.explanation': [
          { required: true, message: 'Please enter explanation', trigger: 'blur' },
          { min: 5, max: 1000, message: 'Explanation length should be 5 to 1000 characters', trigger: 'blur' }
        ]
      },
      uploadUrl: '',
      uploadHeaders: {},
      uploadData: {}
    }
  },
  computed: {
    isEditing() {
      return this.answer && (this.answer.content || this.answer.content_type)
    }
  },
  watch: {
    visible(newVal) {
      this.dialogVisible = newVal
      if (newVal) {
        this.initializeForm()
      }
    },
    dialogVisible(newVal) {
      this.$emit('update:visible', newVal)
    }
  },
  methods: {
    initializeForm() {
      if (this.answer) {
        // Editing existing answer
        this.answerForm = {
          content_type: this.answer.content_type || 'text',
          content: this.answer.content || this.answer.quote_content || this.answer.quote || '',
          fileList: this.answer.fileList || [],
          quiz_content: {
            question: this.answer.quiz_content && this.answer.quiz_content.question ? this.answer.quiz_content.question : '',
            options: this.answer.quiz_content && this.answer.quiz_content.options ? this.answer.quiz_content.options : ['', '', '', ''],
            correct_answer: this.answer.quiz_content && this.answer.quiz_content.correct_answer ? this.answer.quiz_content.correct_answer : '',
            explanation: this.answer.quiz_content && this.answer.quiz_content.explanation ? this.answer.quiz_content.explanation : ''
          },
          is_hidden: this.answer.is_hidden || false
        }
        
        // Initialize content store
        this.contentStore = {
          text: this.answer.content || '',
          quote: this.answer.content || this.answer.quote_content || this.answer.quote || '',
          image: { 
            url: (this.answer.content_type === 'image' && this.answer.fileList && this.answer.fileList.length > 0) ? this.answer.fileList[0].url : '',
            fileList: (this.answer.content_type === 'image') ? (this.answer.fileList || []) : []
          },
          video: { 
            url: (this.answer.content_type === 'video' && this.answer.fileList && this.answer.fileList.length > 0) ? this.answer.fileList[0].url : '',
            fileList: (this.answer.content_type === 'video') ? (this.answer.fileList || []) : []
          },
          quiz: {
            question: (this.answer.quiz_content && this.answer.quiz_content.question) ? this.answer.quiz_content.question : '',
            options: (this.answer.quiz_content && this.answer.quiz_content.options) ? this.answer.quiz_content.options : ['', '', '', ''],
            correct_answer: (this.answer.quiz_content && this.answer.quiz_content.correct_answer) ? this.answer.quiz_content.correct_answer : '',
            explanation: (this.answer.quiz_content && this.answer.quiz_content.explanation) ? this.answer.quiz_content.explanation : ''
          }
        }
        
        this.previousContentType = this.answer.content_type || 'text'
        
        // Set editor type
        if (this.answer.editor_type) {
          this.editorType = this.answer.editor_type
        } else if (this.answer.content_type === 'text' && this.answer.content) {
          if (this.answer.content.includes('<p>') || this.answer.content.includes('<div>') || this.answer.content.includes('<strong>')) {
            this.editorType = 'richtext'
          } else if (this.answer.content.includes('**') || this.answer.content.includes('##') || this.answer.content.includes('- ')) {
            this.editorType = 'markdown'
          } else {
            this.editorType = 'plain'
          }
        } else {
          this.editorType = 'plain'
        }

        // Set media URLs for display
        if (this.answer.content_type === 'image' && this.answer.fileList && this.answer.fileList.length > 0) {
          this.imageUrl = this.answer.fileList[0].url
        }
        if (this.answer.content_type === 'video' && this.answer.fileList && this.answer.fileList.length > 0) {
          this.videoUrl = this.answer.fileList[0].url
        }
        if (this.answer.content_type === 'youtube' && this.answer.content) {
          this.youtubePreviewId = this.extractYoutubeId(this.answer.content) || ''
        }
      } else {
        // Creating new answer
        this.resetForm()
      }
    },

    resetForm() {
      this.answerForm = {
        content_type: 'text',
        content: '',
        fileList: [],
        quiz_content: {
          question: '',
          options: ['', '', '', ''],
          correct_answer: '',
          explanation: ''
        },
        is_hidden: false
      }
      this.editorType = 'plain'
      this.imageUrl = ''
      this.videoUrl = ''

      this.contentStore = {
        text: '',
        quote: '',
        image: { url: '', fileList: [] },
        video: { url: '', fileList: [] },
        quiz: {
          question: '',
          options: ['', '', '', ''],
          correct_answer: '',
          explanation: ''
        }
      }
      this.previousContentType = 'text'
    },

    handleContentTypeChange(newType) {
      // Save current content before switching
      if (this.previousContentType && this.previousContentType !== newType) {
        this.saveCurrentContent(this.previousContentType)
      }
      
      // Restore content for the new type
      this.restoreContent(newType)
      this.previousContentType = newType
    },

    saveCurrentContent(type) {
      if (type === 'text') {
        this.contentStore.text = this.answerForm.content
      } else if (type === 'quote') {
        this.contentStore.quote = this.answerForm.content
      } else if (type === 'image') {
        this.contentStore.image = {
          url: this.imageUrl,
          fileList: this.answerForm.fileList
        }
      } else if (type === 'video') {
        this.contentStore.video = {
          url: this.videoUrl,
          fileList: this.answerForm.fileList
        }
      } else if (type === 'quiz') {
        this.contentStore.quiz = { ...this.answerForm.quiz_content }
      }
    },

    restoreContent(type) {
      if (type === 'text') {
        this.answerForm.content = this.contentStore.text
      } else if (type === 'quote') {
        this.answerForm.content = this.contentStore.quote
      } else if (type === 'image') {
        this.imageUrl = this.contentStore.image.url
        this.answerForm.fileList = this.contentStore.image.fileList
      } else if (type === 'video') {
        this.videoUrl = this.contentStore.video.url
        this.answerForm.fileList = this.contentStore.video.fileList
      } else if (type === 'quiz') {
        this.answerForm.quiz_content = { ...this.contentStore.quiz }
      }
    },

    async handleSave() {
      if (this.saving) return

      this.$refs.answerForm.validate(async (valid) => {
        if (!valid) return

        // Additional validation for quiz
        if (this.answerForm.content_type === 'quiz') {
          const filledOptions = this.answerForm.quiz_content.options.filter(opt => opt.trim() !== '')
          if (filledOptions.length < 2) {
            this.$message.error('Please provide at least 2 quiz options')
            return
          }
          // Validate correct answer is selected
          if (!this.answerForm.quiz_content.correct_answer) {
            this.$message.error('Please select the correct answer')
            return
          }
          // Validate that the selected correct answer corresponds to a filled option
          const correctAnswerIndex = ['A', 'B', 'C', 'D'].indexOf(this.answerForm.quiz_content.correct_answer)
          if (correctAnswerIndex === -1 || !this.answerForm.quiz_content.options[correctAnswerIndex] || this.answerForm.quiz_content.options[correctAnswerIndex].trim() === '') {
            this.$message.error('The selected correct answer option must not be empty')
            return
          }
        }

        // Additional validation for media
        if ((this.answerForm.content_type === 'image' || this.answerForm.content_type === 'video') &&
          (!this.answerForm.fileList || this.answerForm.fileList.length === 0)) {
          this.$message.error(`Please upload a ${this.answerForm.content_type}`)
          return
        }

        this.saving = true

        try {
          // Prepare answer data
          const answerData = {
            ...this.answerForm,
            editor_type: this.answerForm.content_type === 'text' ? this.editorType : undefined
          }

          // Emit save event with answer data
          this.$emit('save', answerData)
          
          // Close dialog
          this.dialogVisible = false
          this.$emit('close')
        } catch (error) {
          console.error('Error saving answer:', error)
          this.$message.error('Failed to save answer')
        } finally {
          this.saving = false
        }
      })
    },

    handleClose() {
      this.dialogVisible = false
      this.$emit('close')
    },

    // Image upload methods
    triggerFileInput() {
      this.$refs.fileInput.click()
    },

    async handleImageChange(event) {
      const file = event.target.files[0]
      if (!file) return

      if (!file.type.startsWith('image/')) {
        this.$message.error('Please select an image file')
        return
      }

      if (file.size > 5 * 1024 * 1024) {
        this.$message.error('Image size should be less than 5MB')
        return
      }

      try {
        // Compress image if needed (target: 500KB-1MB)
        let processedFile = file
        if (file.size > 500 * 1024) { // If larger than 500KB, compress
          this.$message.info('Compressing image...')
          processedFile = await this.compressImage(file, 1024 * 1024) // Target max 1MB
        }

        // Get upload token
        const tokenResponse = await postRequest('getSingaporeQiniuToken', {})

        if (tokenResponse.data.code !== 0) {
          this.$message.error('Failed to get upload token')
          return
        }

        const token = tokenResponse.data.data
        // Let Qiniu auto-generate hash-based key
        const qiniuKey = null

        // Upload to Qiniu with putExtra to specify folder prefix
        const putExtra = {
          fname: processedFile.name,
          mimeType: processedFile.type
        }
        const config = {
          useCdnDomain: true
        }
        const observable = qiniu.upload(processedFile, qiniuKey, token, putExtra, config)

        observable.subscribe({
          next: (res) => {
            // Upload progress
          },
          error: (err) => {
            console.error('Upload error:', err)
            this.$message.error('Failed to upload image')
          },
          complete: (res) => {
            const imageUrl = `https://ap.knowit.today/${res.key}`
            this.imageUrl = imageUrl
            this.answerForm.fileList = [{
              url: imageUrl,
              name: file.name,
              mime_type: file.type
            }]
            this.$message.success('Image uploaded successfully')
          }
        })
      } catch (error) {
        console.error('Error uploading image:', error)
        this.$message.error('Failed to upload image')
      }
    },

    // Video upload methods
    beforeVideoUpload(file) {
      const isVideo = file.type.startsWith('video/')
      const isLt50M = file.size / 1024 / 1024 < 50

      if (!isVideo) {
        this.$message.error('Please select a video file')
        return false
      }
      if (!isLt50M) {
        this.$message.error('Video size should be less than 50MB')
        return false
      }
      return true
    },

    async handleVideoSuccess(response, file) {
      try {
        // Get upload token
        const tokenResponse = await postRequest('getSingaporeQiniuToken', {})

        if (tokenResponse.data.code !== 0) {
          this.$message.error('Failed to get upload token')
          return
        }

        const token = tokenResponse.data.data
        // Let Qiniu auto-generate hash-based key
        const qiniuKey = null

        // Upload to Qiniu with putExtra to specify folder prefix
        const putExtra = {
          fname: file.name,
          mimeType: file.type
        }
        const config = {
          useCdnDomain: true
        }
        const observable = qiniu.upload(file.raw, qiniuKey, token, putExtra, config)

        observable.subscribe({
          next: (res) => {
            // Upload progress
          },
          error: (err) => {
            console.error('Upload error:', err)
            this.$message.error('Failed to upload video')
          },
          complete: (res) => {
            const videoUrl = `https://ap.knowit.today/${res.key}`
            this.videoUrl = videoUrl
            this.answerForm.fileList = [{
              url: videoUrl,
              name: file.name,
              mime_type: file.type
            }]
            this.$message.success('Video uploaded successfully')
          }
        })
      } catch (error) {
        console.error('Error uploading video:', error)
        this.$message.error('Failed to upload video')
      }
    },

    // Image compression method
    async compressImage(file, maxSize) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.readAsDataURL(file)
        reader.onload = (e) => {
          const img = new Image()
          img.src = e.target.result
          img.onload = () => {
            const canvas = document.createElement('canvas')
            const ctx = canvas.getContext('2d')

            // Calculate new dimensions while maintaining aspect ratio
            let width = img.width
            let height = img.height

            // Start with reasonable max dimensions (1920x1920)
            const maxDimension = 1920
            if (width > maxDimension || height > maxDimension) {
              if (width > height) {
                height = Math.round((height * maxDimension) / width)
                width = maxDimension
              } else {
                width = Math.round((width * maxDimension) / height)
                height = maxDimension
              }
            }

            canvas.width = width
            canvas.height = height
            ctx.drawImage(img, 0, 0, width, height)

            // Try different quality levels to reach target size
            let quality = 0.9
            const tryCompress = () => {
              canvas.toBlob((blob) => {
                if (blob.size <= maxSize || quality <= 0.5) {
                  // Success or reached minimum quality
                  const compressedFile = new File([blob], file.name, {
                    type: 'image/jpeg',
                    lastModified: Date.now()
                  })
                  const sizeMB = (blob.size / (1024 * 1024)).toFixed(2)
                  this.$message.success(`Image compressed to ${sizeMB}MB`)
                  resolve(compressedFile)
                } else {
                  // Try again with lower quality
                  quality -= 0.1
                  tryCompress()
                }
              }, 'image/jpeg', quality)
            }

            tryCompress()
          }
          img.onerror = () => reject(new Error('Failed to load image'))
        }
        reader.onerror = () => reject(new Error('Failed to read file'))
      })
    },

    extractYoutubeId(url) {
      if (!url) return null
      const patterns = [
        /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/v\/)([a-zA-Z0-9_-]{11})/,
        /^([a-zA-Z0-9_-]{11})$/
      ]
      for (const pattern of patterns) {
        const match = url.match(pattern)
        if (match) return match[1]
      }
      return null
    },

    handleYoutubeUrlInput(val) {
      this.youtubePreviewId = this.extractYoutubeId(val) || ''
    },

    // Audio recording methods
    addTextToContent(text) {
      if (this.answerForm.content) {
        this.answerForm.content += '\n' + text
      } else {
        this.answerForm.content = text
      }
    },

    addTextToQuizQuestion(text) {
      if (this.answerForm.quiz_content.question) {
        this.answerForm.quiz_content.question += ' ' + text
      } else {
        this.answerForm.quiz_content.question = text
      }
    }
  }
}
</script>

<style scoped>
.custom-post-answer-dialog {
  /* Add any custom styles if needed */
}

.dialog-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0;
}

.dialog-title {
  font-size: 18px;
  font-weight: 600;
}

.dialog-header-buttons {
  display: flex;
  gap: 10px;
}

.content-type-row {
  margin-bottom: 20px;
}

.content-textarea-item,
.markdown-editor-item,
.richtext-editor-item {
  margin-bottom: 20px;
}

.audio-recording-wrapper {
  margin-bottom: 10px;
}

.image-upload-container {
  cursor: pointer;
}

.upload-area {
  border: 2px dashed #dcdfe6;
  border-radius: 4px;
  padding: 40px;
  text-align: center;
  transition: border-color 0.3s;
}

.upload-area:hover {
  border-color: #409eff;
}

.upload-area.has-image {
  border: none;
  padding: 0;
}

.uploaded-image {
  width: 100%;
  max-height: 400px;
  object-fit: contain;
  border-radius: 4px;
}

.upload-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #909399;
}

.upload-icon {
  font-size: 48px;
  margin-bottom: 10px;
}

.upload-text {
  margin: 0;
}

.upload-tip {
  margin-top: 10px;
  font-size: 12px;
  color: #909399;
}

.video-uploader {
  width: 100%;
}

.uploaded-video {
  width: 100%;
  max-height: 400px;
  border-radius: 4px;
}

.quiz-option-input {
  margin-bottom: 10px;
}

.correct-answer-section {
  display: flex;
  align-items: center;
  gap: 15px;
  padding: 15px;
  background-color: #f5f7fa;
  border-radius: 4px;
  margin-bottom: 10px;
}

.correct-answer-label {
  font-weight: 500;
  color: #606266;
  white-space: nowrap;
}

.no-label-form {
  /* Remove default form spacing if needed */
}

.form-item-no-label {
  margin-bottom: 20px;
}

.youtube-preview {
  margin-top: 4px;
}

.youtube-error {
  margin-top: 8px;
  color: #e6a23c;
  font-size: 13px;
}
</style>
